// MapSurface.tsx — /map, the team topic graph (dashboard overhaul §5.2 / Phase 4).
//
// Team-only (D5: the individual/team toggle is gone). Reuses the existing Canvas2D + d3-force
// engine (MapCanvas/useMapInteraction) unchanged; this file owns the chrome. Redesign port
// 2026-07: prototype knowledge-page look — PageMast masthead with NOTES/PROJECTS tiles, the
// Graph/List pill toggle, and the graph mounted on the DARK PURPLE panel (theme-invariant, like
// the brief's Around band), with the canvas colors tuned for it. All deep-link/filter/highlight
// state logic is unchanged.

import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { readListParams, writeListParams, type MapListParams } from '../../lib/map-list-params';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { MapCanvas } from '../map/MapCanvas';
import { MapControls } from '../map/MapControls';
import { MapDetailPanel } from '../map/MapDetailPanel';
import { MapListView } from './MapListView';
import { useMapInteraction } from '../map/useMapInteraction';
import { AskLauncher } from '../shared/AskLauncher';
import { PageMast, MastTile } from '../shared/workroom';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { MapSurfaceData } from '../../hooks/useMapSurface';
import { useTeamProjects } from '../../hooks/useTeamProjects';
import { buildClusterIndexMap, clusterColor } from '../../lib/clusters';
import { PERSON_PALETTE } from '../../lib/constants';
import { SITE_COLOR } from '../../lib/sites';

interface MapSurfaceProps {
  data: MapSurfaceData;
  onNav: (id: string) => void;
}

// Canvas colors for the redesign's dark purple map panel. The panel is theme-invariant (like the
// brief's Around band), so these are constants — no theme observer needed. VISIBILITY is the
// requirement: cream edges at a clearly-visible alpha, full-opacity cream labels, and a dimmed
// tone that stays present (the prototype's unclustered purple-grey).
const PANEL_GRAPH_COLORS = {
  edge: 'rgba(251, 245, 233, 0.30)',
  label: '#fbf5e9',
  dimmed: '#6b6386',
};

export function MapSurface({ data, onNav }: MapSurfaceProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The canvas element ALSO lives in state: MapCanvas unmounts on the List toggle and remounts
  // on the way back, and useMapInteraction's listener effect can only notice the new element if
  // its identity is a dependency — a bare ref mutation leaves the rebuilt canvas inert (bug-451).
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const attachCanvas = useCallback((el: HTMLCanvasElement | null) => {
    canvasRef.current = el;
    setCanvasEl(el);
  }, []);
  const simulationRef = useRef<any>(null);
  const nodesRef = useRef<any[]>([]);
  const location = useLocation();

  // A source deep-link (Sources → "View …") lands here as ?view=list&source=<site>. `focusSite`
  // narrows the List to one source; ?view forces the List layout.
  const focusSite = useMemo(() => new URLSearchParams(location.search).get('source'), [location.search]);

  // List UI state deep-link/restore (note-redesign PR2): search/group/focus live in the URL so a
  // detail-page tag click lands on a filtered list, and back-navigation restores it.
  const [, setSearchParams] = useSearchParams();
  const initialListParams = useMemo(() => readListParams(new URLSearchParams(location.search)),
    // Init-only: MapListView reads this on mount; later URL writes come FROM the list itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const onListParams = useCallback((p: MapListParams) => {
    setSearchParams(prev => writeListParams(prev, p), { replace: true });
  }, [setSearchParams]);

  // ?view is now written by the toggle, so an EXPLICIT view=graph outranks the implicit "these
  // list-ish params mean the user wants the list" inference. Without that precedence, choosing Graph
  // while a search was active would immediately snap back to List: the URL write re-fires the
  // deep-link effect below, which would still see ?search and force the list again.
  const hasListState = (p: URLSearchParams) => {
    const view = p.get('view');
    if (view === 'list') return true;
    if (view === 'graph') return false;
    return !!p.get('source') || !!p.get('search') || !!p.get('group') || !!p.get('focus');
  };

  const [viewLayout, setViewLayoutState] = useState<'graph' | 'list'>(() =>
    hasListState(new URLSearchParams(location.search)) ? 'list' : 'graph');

  // Writing the choice into the URL is what makes it survive a round trip. The layout used to be
  // component state ONLY: open a note from the list, press back, and you landed on the graph —
  // ?view was read on arrival but never written when the user picked. (It self-healed the moment a
  // search or focus was set, because those params force the list; with neither, it did not.)
  const setViewLayout = useCallback((next: 'graph' | 'list') => {
    setViewLayoutState(next);
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('view', next);
      return sp;
    }, { replace: true });
  }, [setSearchParams]);

  // A deep-link can arrive while this surface is already mounted (same route, new query), so
  // re-honor ?view=list / ?source / list-state params on a search change. Only ever force TO list —
  // never back to graph — so the user's manual Graph/List toggle isn't overridden on an unrelated
  // re-render. The list's OWN URL write-backs also land here and are no-ops (already in list view).
  useEffect(() => {
    if (hasListState(new URLSearchParams(location.search))) setViewLayoutState('list');
  }, [location.search]);

  // D6: hidden projects (read from the team/projects flags) drop out of BOTH the graph and the list.
  // (Named teamProjects to avoid colliding with the graph-cluster `projects` memo below.)
  const { projects: teamProjects } = useTeamProjects();
  const hiddenIds = useMemo(() => new Set(teamProjects.filter(p => p.hidden).map(p => p.id)), [teamProjects]);

  // Team graph: project identity comes from the team_project_* fields; hidden projects' nodes are
  // filtered out, and any edge whose endpoint vanished is dropped so the sim never references it.
  const renderNodes = useMemo(() =>
    data.nodes
      .filter(n => !(n.team_project_id && hiddenIds.has(n.team_project_id)))
      .map(n => ({
        ...n,
        label: n.title || n.topic,
        r: 0,
        project_id: n.team_project_id || null,
        project_name: n.team_project_name || n.project_name || null,
      })),
    [data.nodes, hiddenIds]
  );
  const renderEdges = useMemo(() => {
    const visible = new Set(renderNodes.map(n => n.id));
    return data.edges.filter(e => visible.has(e.source) && visible.has(e.target));
  }, [data.edges, renderNodes]);

  const adjacency = useMemo(() => {
    const adj = new Map<string, Set<string>>();
    for (const edge of renderEdges) {
      if (!adj.has(edge.source)) adj.set(edge.source, new Set());
      if (!adj.has(edge.target)) adj.set(edge.target, new Set());
      adj.get(edge.source)!.add(edge.target);
      adj.get(edge.target)!.add(edge.source);
    }
    return adj;
  }, [renderEdges]);

  const clusterIndexMap = useMemo(() => buildClusterIndexMap(renderNodes), [renderNodes]);

  const projects = useMemo(() => {
    const seen = new Map<string, string>();
    for (const node of renderNodes) {
      if (node.project_id && !seen.has(node.project_id)) {
        seen.set(node.project_id, node.project_name || 'Untitled');
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({
      id,
      name,
      color: clusterColor(clusterIndexMap.get(id)!),
    }));
  }, [renderNodes, clusterIndexMap]);

  const persons = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ name: string; color: string }> = [];
    for (const node of renderNodes) {
      if (node.author_name && !seen.has(node.author_name)) {
        seen.add(node.author_name);
        result.push({ name: node.author_name, color: PERSON_PALETTE[result.length % PERSON_PALETTE.length] });
      }
    }
    return result;
  }, [renderNodes]);

  const personColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of persons) map.set(p.name, p.color);
    return map;
  }, [persons]);

  // Only offer sources that actually have nodes in the current graph — a source with zero
  // conversations here is a dead filter option (picking it always yields an empty graph).
  const sites = useMemo(() => {
    const present = new Set(renderNodes.map(n => n.site));
    return Object.keys(SITE_COLOR).filter(site => present.has(site));
  }, [renderNodes]);

  const [viewMode, setViewMode] = useState('all');
  // viewMode can go stale (e.g. the only project for the selected source gets hidden while
  // viewing the List layout, dropping it out of `sites`) — clear it back to 'all' rather than
  // leaving a selection the dropdown no longer offers. Clearing the actual state (not just
  // masking it at render time) matters: if the source later reappears (project un-hidden),
  // a merely-derived fallback would silently re-apply the old filter with no user action.
  useEffect(() => {
    if (viewMode !== 'all' && !sites.includes(viewMode)) {
      setViewMode('all');
    }
  }, [sites, viewMode]);
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [personFilter, setPersonFilter] = useState<string | null>(null);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleNodeClick = useCallback((id: string) => setDetailNodeId(id), []);
  const handleNodeRightClick = useCallback((id: string, _x: number, _y: number) => setDetailNodeId(id), []);

  // webapp-actions #5: the map's delete button had a fully-built backend (DELETE /api/conversations/[id],
  // which scopes to the caller's OWN conversation via mutateOwnedConversation) but was wired to a no-op.
  // A delete on a node the caller doesn't own is rejected server-side — the ownership check is the guard.
  //
  // This RETURNS the promise rather than swallowing failure into a console.error, so the panel can
  // wait for the server's answer before closing. The button renders on every node of a team-wide
  // map, so the rejected case is the common one, and it used to be indistinguishable from success.
  const handleDeleteNode = useCallback(async (id: string) => {
    await apiFetch(`/api/conversations/${id}`, { method: 'DELETE' });
    await queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-map'] });
  }, [queryClient]);

  const { state: interaction, zoomIn, zoomOut, resetZoom, focusOnNodes } = useMapInteraction(
    canvasRef, canvasEl, simulationRef, nodesRef, adjacency, handleNodeClick, handleNodeRightClick,
  );

  const isInteracting = interaction.isDragging || interaction.isPanning || !!interaction.hoveredNodeId;

  // Answer→graph handoff: the Ask surface's "◉ Show in map" action deep-links here as
  // /map?highlight=<id,id,id>. Those ids are highlighted and framed on the graph. URL-driven (was the
  // in-memory ask.turns coupling), so the framing is now deep-linkable from an answer, a briefing, or a
  // pasted link. `highlightKey` is the raw param — a distinct param means a distinct set to frame once.
  const highlightKey = useMemo(() => new URLSearchParams(location.search).get('highlight'), [location.search]);
  const highlightIds = useMemo(() => {
    if (!highlightKey) return null;
    const ids = highlightKey.split(',').map(s => s.trim()).filter(Boolean);
    return ids.length > 0 ? new Set(ids) : null;
  }, [highlightKey]);

  // The highlight dims every other node, so it is a filter as far as the user is concerned — but it
  // lived only in the URL and the Reset control never knew about it, leaving the map dimmed with no
  // visible way out. Clearing it is a URL edit, which also makes the cleared state survive a reload.
  const clearHighlight = useCallback(() => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('highlight');
      return sp;
    }, { replace: true });
  }, [setSearchParams]);

  // Mobile: force the list. The d3-force canvas has no touch handlers, so the graph is
  // unusable on a phone — render MapListView instead and never mount MapCanvas (so the sim
  // + ResizeObserver never run). Interactive-graph-on-touch is deferred, not attempted.
  const isMobile = useIsMobile();
  const effectiveLayout: 'graph' | 'list' = isMobile ? 'list' : viewLayout;

  // Frame the ?highlight= sources on the graph, ONCE per distinct set, the first time the graph is shown.
  // focusOnNodes reads node positions from nodesRef, which MapCanvas populates only after its sim runs — so
  // wait (bounded) for positions before framing, and never re-frame the same set.
  const focusedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (effectiveLayout !== 'graph' || !highlightKey || !highlightIds) return;
    if (focusedKeyRef.current === highlightKey) return;
    let raf = 0;
    let tries = 0;
    const attempt = () => {
      if (nodesRef.current.length > 0) {
        focusOnNodes(Array.from(highlightIds));
        focusedKeyRef.current = highlightKey; // frame this answer exactly once
        return;
      }
      if (tries++ < 120) raf = requestAnimationFrame(attempt); // ~2s max, then give up
    };
    raf = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(raf);
  }, [effectiveLayout, highlightKey, highlightIds, focusOnNodes]);

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <PageMast
          eyebrow="Knowledge · the shared map"
          title={<>What the team <em>knows.</em></>}
          intro="Every source, every teammate - distilled into notes, clustered into projects, linked by what’s actually related. The brief, Ask, and the record all draw on this map."
          tiles={<>
            <MastTile
              label="NOTES"
              value={renderNodes.length}
              note={sites.length > 0 ? `distilled from ${sites.length} source${sites.length === 1 ? '' : 's'}` : 'distilled conversations'}
            />
            <MastTile
              label="PROJECTS"
              value={projects.length}
              note={`${persons.length} ${persons.length === 1 ? 'person' : 'people'} contributing`}
            />
          </>}
        />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full border border-border bg-card p-1">
            {/* Graph is desktop-only (no touch handlers); List works everywhere. Ask is its own surface now. */}
            {!isMobile && (
              <button
                onClick={() => setViewLayout('graph')}
                className={`rounded-full px-3.5 py-1 text-sm transition ${effectiveLayout === 'graph' ? 'bg-foreground text-card' : 'text-muted-foreground hover:text-foreground'}`}
              >
                ◉ Graph
              </button>
            )}
            <button
              onClick={() => setViewLayout('list')}
              className={`rounded-full px-3.5 py-1 text-sm transition ${effectiveLayout === 'list' ? 'bg-foreground text-card' : 'text-muted-foreground hover:text-foreground'}`}
            >
              ☰ List
            </button>
          </div>
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[.1em] text-muted-foreground">
            {renderNodes.length} conversations · {projects.length} projects · {persons.length} {persons.length === 1 ? 'person' : 'people'}
          </span>
        </div>

        {/* Ask entry point: submitting routes to /ask?q=<question>, where the answer streams on its own surface. */}
        <AskLauncher onNav={onNav} />

        {effectiveLayout === 'graph' ? (
          renderNodes.length === 0 ? (
            <div className="mt-1 rounded-[24px] border border-dashed border-[#ddd2c0] bg-card/60 px-6 py-14 text-center dark:border-border">
              <h2 className="text-[19px] font-semibold leading-snug tracking-[-.01em]">
                {data.nodes.length === 0 ? 'Team conversation graph is growing' : 'All projects are hidden'}
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-muted-foreground">
                {data.nodes.length === 0
                  ? 'As your team captures more conversations, connections will appear here.'
                  : 'Every project is hidden from the map. Un-hide one to bring it back.'}
              </p>
            </div>
          ) : (
          <>
            <div className="relative overflow-hidden rounded-[24px] border border-[#2a2447] bg-[#322b4d] shadow-[0_18px_38px_rgba(74,58,107,.16)]"
              style={{ height: 'min(64vh, 600px)', minHeight: 420 }}
            >
              <MapCanvas
                canvasRef={canvasRef}
                attachCanvas={attachCanvas}
                nodes={renderNodes}
                edges={renderEdges}
                simulationRef={simulationRef}
                nodesRef={nodesRef}
                hoveredNodeId={interaction.hoveredNodeId}
                adjacency={adjacency}
                transform={interaction.transform}
                viewMode={viewMode}
                projectFilter={projectFilter}
                personFilter={personFilter}
                personColors={personColorMap}
                isInteracting={isInteracting}
                themeColors={PANEL_GRAPH_COLORS}
                highlightedIds={highlightIds}
              />
              <MapControls
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                sites={sites}
                projectFilter={projectFilter}
                onProjectFilterChange={setProjectFilter}
                projects={projects}
                personFilter={personFilter}
                onPersonFilterChange={setPersonFilter}
                persons={persons}
                highlightActive={!!highlightIds}
                onClearHighlight={clearHighlight}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onResetZoom={resetZoom}
              />
              {detailNodeId && (
                <MapDetailPanel
                  nodeId={detailNodeId}
                  nodes={renderNodes}
                  onClose={() => setDetailNodeId(null)}
                  onDelete={handleDeleteNode}
                  onNav={onNav}
                />
              )}
            </div>
            <p className="mt-3 text-[12px] leading-5 text-muted-foreground">
              Nodes = conversations across the whole team (private excluded). Solid links = semantic
              similarity (≥0.70); dashed links = same repo. Click a node to open it; hover to trace its connections.
            </p>
          </>
          )
        ) : (
          <MapListView projects={teamProjects} hiddenIds={hiddenIds} onNav={onNav} focusSite={focusSite}
            initialParams={initialListParams} onParamsChange={onListParams} />
        )}
      </div>
    </div>
  );
}
